import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Avatar from './Avatar.jsx';
import { placeName, timeLabel } from '../lib/format.js';

const MAX_BODY = 300;

/**
 * The public room — one column, newest at the bottom, the way a stream chat
 * reads. It follows the tail unless you have scrolled up to read something,
 * in which case it holds still and offers to jump back down.
 */
export default function LiveChat({
  entries, actors, selfActorId, social, bounds, onSend, onPickActor, collapsed, onToggle,
}) {
  // Presence lines keep a snapshot of who joined, but a place name arrives a
  // moment later from the reverse lookup — so prefer the live actor while
  // they are still on the map, and fall back to the snapshot once they leave.
  const live = useMemo(() => new Map((actors ?? []).map((a) => [a.id, a])), [actors]);
  const logRef = useRef(null);
  const inputRef = useRef(null);
  const [pinned, setPinned] = useState(true);
  const [behind, setBehind] = useState(0);
  const [scope, setScope] = useState('everyone');

  const friends = useMemo(() => new Set((social?.friends ?? []).map((friend) => friend.actorId)), [social]);
  // Presence lines no longer carry a point, so "nearby" is answered from who
  // is on the map right now. Somebody who has left cannot be placed, and is
  // simply not nearby — which is the honest answer rather than a stored one.
  const isNearby = (entry) => {
    const id = entry.actor?.id ?? entry.actorId;
    const person = live.get(id);
    if (!bounds || ![person?.lat, person?.lng].every(Number.isFinite)) return false;
    const latitude = person.lat >= bounds.south && person.lat <= bounds.north;
    const longitude = bounds.west <= bounds.east
      ? person.lng >= bounds.west && person.lng <= bounds.east
      : person.lng >= bounds.west || person.lng <= bounds.east;
    return latitude && longitude;
  };

  const visibleEntries = useMemo(() => {
    const scoped = entries.filter((entry) => {
      if (scope === 'everyone') return true;
      const actorId = entry.actor?.id ?? entry.actorId;
      if (actorId === selfActorId) return true;
      if (scope === 'friends') return friends.has(actorId);
      return isNearby(entry);
    });

    const output = [];
    let ambient = [];
    const flush = () => {
      if (!ambient.length) return;
      // Repeated state from one actor is summarisable: only the latest doing
      // in this gap between things people actually said is useful.
      const latestDoing = new Map();
      ambient.forEach((entry, index) => {
        if (entry.kind === 'doing') latestDoing.set(entry.actor?.id, index);
      });
      const material = ambient.filter((entry, index) => (
        entry.kind !== 'doing' || latestDoing.get(entry.actor?.id) === index
      ));
      if (material.length <= 3) output.push(...material);
      else output.push({
        id: `ambient-${material[0].id}-${material.at(-1).id}`,
        kind: 'ambient-group', entries: material,
      });
      ambient = [];
    };
    for (const entry of scoped) {
      if (entry.kind) ambient.push(entry);
      else { flush(); output.push(entry); }
    }
    flush();
    return output;
  }, [entries, scope, selfActorId, friends, bounds]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const log = logRef.current;
    if (!log) return;
    if (pinned) {
      log.scrollTop = log.scrollHeight;
      setBehind(0);
    } else {
      setBehind((n) => n + 1);
    }
  }, [entries.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const onScroll = () => {
      const atBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 40;
      setPinned(atBottom);
      if (atBottom) setBehind(0);
    };
    log.addEventListener('scroll', onScroll, { passive: true });
    return () => log.removeEventListener('scroll', onScroll);
  }, []);

  const jumpDown = () => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
    setPinned(true);
    setBehind(0);
  };

  const submit = (ev) => {
    ev.preventDefault();
    const body = inputRef.current.value.trim();
    if (!body) return;
    onSend(body);
    inputRef.current.value = '';
    jumpDown();
  };

  if (collapsed) {
    return (
      <button type="button" className="livechat-reopen" onClick={onToggle}>
        💬 Live chat
      </button>
    );
  }

  return (
    <section className="livechat" aria-label="Live chat">
      <header className="livechat-head">
        <span className="livechat-title">
          <span className="pulse-dot" /> Live chat
        </span>
        <div className="room-controls">
          {['everyone', 'nearby', 'friends'].map((value) => (
            <button key={value} type="button" className={`room-scope ${scope === value ? 'on' : ''}`} onClick={() => setScope(value)}>
              {value[0].toUpperCase() + value.slice(1)}
            </button>
          ))}
          <button type="button" className="icon-btn" onClick={onToggle} aria-label="Hide live chat">×</button>
        </div>
      </header>

      <div className="livechat-log" ref={logRef}>
        {visibleEntries.length === 0 && (
          <p className="empty-note">Nobody has said anything yet. Go first.</p>
        )}

        {visibleEntries.map((e) => (
          e.kind ? (
            // Presence, not speech — rendered the way a group thread shows
            // "X joined": quiet, centred, out of the conversation's way.
            <SystemLine
              key={e.id}
              entry={e}
              person={e.actor ? (live.get(e.actor.id) ?? e.actor) : null}
              stillHere={!!e.actor && live.has(e.actor.id)}
              onPickActor={onPickActor}
            />
          ) : (
          <div key={e.id} className={`liveline ${e.actorId === selfActorId ? 'mine' : ''}`}>
            <button
              type="button"
              className="liveline-who"
              onClick={() => onPickActor?.(e.actorId)}
              title={e.username ? `@${e.username}` : 'guest'}
            >
              <Avatar person={e} size="sm" online={live.has(e.actorId)} />
            </button>
            <span className="liveline-body">
              <b style={{ color: e.color }}>{e.displayName}</b>{' '}
              {e.body}
              <span className="liveline-time">{timeLabel(e.at)}</span>
            </span>
          </div>
          )
        ))}
      </div>

      {behind > 0 && (
        <button type="button" className="livechat-jump" onClick={jumpDown}>
          {behind} new ↓
        </button>
      )}

      <form className="livechat-form" onSubmit={submit}>
        <input
          ref={inputRef}
          className="chat-input"
          maxLength={MAX_BODY}
          placeholder="Say something to everyone…"
        />
        <button type="submit" className="send-btn" aria-label="Send">↑</button>
      </form>
    </section>
  );
}

/**
 * "indigo_lynx joined from Paris, FR" — the name pans the globe to them,
 * but only while they are still there to pan to.
 */
function SystemLine({ entry, person, stillHere, onPickActor }) {
  if (entry.kind === 'ambient-group') {
    const arrivals = entry.entries.filter((item) => item.kind === 'join');
    const cities = [...new Set(arrivals.map((item) => placeName(item.actor)).filter(Boolean))].slice(0, 3);
    const other = entry.entries.length - arrivals.length;
    return (
      <div className="liveline system ambient-group">
        <span>
          {arrivals.length > 0 && `${arrivals.length} ${arrivals.length === 1 ? 'person arrived' : 'people arrived'}`}
          {arrivals.length > 0 && cities.length > 0 && ` from ${cities.join(', ')}`}
          {arrivals.length > 0 && other > 0 && ' · '}
          {other > 0 && `${other} ${other === 1 ? 'other thing is' : 'other things are'} happening`}
        </span>
      </div>
    );
  }
  const place = placeName(person);
  const canFocus = stillHere
    && person.locationSource !== 'fallback'
    && Number.isFinite(person.lat)
    && Number.isFinite(person.lng);
  const name = canFocus ? (
    <button type="button" className="liveline-name" onClick={() => onPickActor?.(person.id)}>
      {person.displayName}
    </button>
  ) : (
    <b>{person.displayName}</b>
  );

  return (
    <div className="liveline system">
      <button
        type="button"
        className="liveline-who"
        onClick={() => onPickActor?.(person.id)}
        disabled={!canFocus}
      >
        <Avatar person={person} size="xs" />
      </button>
      <span>
        {name}{' '}
        {entry.kind === 'join' ? 'joined'
          : entry.kind === 'leave' ? 'left'
          : entry.kind === 'doing' ? `is ${entry.body}`
          : entry.kind === 'post' ? 'posted something'
          : entry.kind === 'live' ? entry.body
          : entry.body}
        {entry.kind === 'join' && place && <> from <span className="liveline-place">{place}</span></>}
      </span>
      <span className="liveline-time">{timeLabel(entry.at)}</span>
    </div>
  );
}
